import type { BlockType, ReportBlock } from "../reporting.service";

export interface BlockMeta {
  type: BlockType;
  label: string;
  /** default block title when the block is first added */
  defaultTitle: string;
  /** empty-state helper copy shown on the canvas before a source is bound */
  helper: string;
  accent: string;
  /** tabler-style icon path (24x24 viewBox) */
  icon: string;
}

export const BLOCK_META: Record<BlockType, BlockMeta> = {
  text: {
    type: "text",
    label: "Text",
    defaultTitle: "Text Block",
    helper: "Use this block to add custom text anywhere in your report.",
    accent: "#6b7280",
    icon: "M4 6h16M4 12h16M4 18h10",
  },
  metric: {
    type: "metric",
    label: "Metric",
    defaultTitle: "Metric Title",
    helper: "Choose a data source to highlight in this key metric.",
    accent: "#5540af",
    icon: "M3 17l6-6 4 4 8-8M21 7v4M21 7h-4",
  },
  chart: {
    type: "chart",
    label: "Chart",
    defaultTitle: "Chart Title",
    helper: "Choose a data source to visualize your data in this chart.",
    accent: "#0891b2",
    icon: "M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-3",
  },
  table: {
    type: "table",
    label: "Table",
    defaultTitle: "Table Title",
    helper: "Choose a data source to display detailed information in this table.",
    accent: "#d97706",
    icon: "M3 5h18v14H3zM3 10h18M3 15h18M9 5v14M15 5v14",
  },
  ai_narrative: {
    type: "ai_narrative",
    label: "AI Narrative",
    defaultTitle: "Narrative Title",
    helper:
      "Choose one or more data sources to generate insights and summaries from your data.",
    accent: "#9333ea",
    icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.5 6.5L20 11l-6.5 2.5L11 20l-2.5-6.5L2 11l6.5-2.5z",
  },
};

export const ADD_BLOCK_ORDER: BlockType[] = [
  "text",
  "metric",
  "chart",
  "table",
  "ai_narrative",
];

let seq = 0;
/** Stable-enough client id for a freshly added block (server keeps its own). */
const newBlockId = (type: BlockType): string => `${type}-${++seq}`;

export const makeBlock = (type: BlockType, order: number): ReportBlock => {
  const meta = BLOCK_META[type];
  const base: ReportBlock = {
    id: newBlockId(type),
    type,
    order,
    title: meta.defaultTitle,
    config: {},
  };
  switch (type) {
    case "text":
      base.config = { body: "" };
      break;
    case "metric":
      base.config = { source: { family: "formula", id: null }, target: null };
      break;
    case "chart":
      base.config = { source: { family: "dashboard", id: null }, chart_type: "bar" };
      break;
    case "table":
      base.config = { source: { family: "doc_insights", id: "codes" }, limit: 10 };
      break;
    case "ai_narrative":
      base.config = {
        scope: [],
        tone: "Donor / external",
        length: "Medium",
        note: "",
      };
      break;
  }
  return base;
};
